import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Modal = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  max-width: 400px;
  width: 100%;
  color: #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  text-align: center;
`;

const Icon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #2c3e50;
  font-weight: 700;
`;

const Description = styled.p`
  font-size: 1rem;
  margin-bottom: 30px;
  color: #7f8c8d;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #4ecdc4;
    transform: translateY(-2px);
    box-shadow: 0 2px 10px rgba(78, 205, 196, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 15px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  
  ${props => props.primary ? `
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
    color: white;
  ` : `
    background: rgba(0, 0, 0, 0.1);
    color: #666;
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 5px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  z-index: 10;
`;

const Spinner = styled.div`
  border: 4px solid rgba(78, 205, 196, 0.3);
  border-radius: 50%;
  border-top: 4px solid #4ecdc4;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PasswordModal = ({ isOpen, onClose, onSuccess, error, isLoading }) => {
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim() || isSubmitting || isLoading) return;
    
    setIsSubmitting(true);
    
    try {
      await onSuccess(password.trim());
    } catch (err) {
      console.error('Password verification failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Backdrop
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Modal
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        {(isLoading || isSubmitting) && (
          <LoadingOverlay>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Spinner />
              <span style={{ fontWeight: 'bold', color: '#333' }}>Verifying...</span>
            </div>
          </LoadingOverlay>
        )}
        
        <Icon>🔒</Icon>
        <Title>{t('privateRoom')}</Title>
        <Description>
          {t('privateRoomDescription')}
        </Description>
        
        <Form onSubmit={handleSubmit}>
          <Input
            type="password"
            placeholder={t('enterPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting || isLoading}
            autoFocus
          />
          
          {error && <ErrorText>{error}</ErrorText>}
          
          <ButtonGroup>
            <Button 
              type="button" 
              onClick={onClose}
              disabled={isSubmitting || isLoading}
            >
              {t('cancel')}
            </Button>
            
            <Button
              type="submit"
              primary
              disabled={!password.trim() || isSubmitting || isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isSubmitting || isLoading ? 'Verifying...' : 'Continue'}
            </Button>
          </ButtonGroup>
        </Form>
      </Modal>
    </Backdrop>
  );
};

export default PasswordModal; 